/**
 * sl-rqa6.6: Feature Locked Inline Banner
 *
 * Reusable inline banner shown when a plan-gated feature is not available.
 * Displays the required plan and a link to billing settings.
 *
 * Usage:
 *   <FeatureLockedBanner featureKey="workflow engine-automations" />
 *
 * SOLID:
 * - S: Only renders the locked feature message
 * - O: New features added via DB seed, no code changes needed
 */
import { Button } from '@affine/component/ui/button';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { useI18n } from '@affine/i18n';
import { InformationFillDuotoneIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import { useFeatureGate } from '../hooks/use-feature-gate';
import * as styles from './feature-locked-banner.css';

interface FeatureLockedBannerProps {
  featureKey: string;
}

/**
 * Inline banner for locked features. Only renders when the feature is not
 * allowed on the current plan. Renders nothing if the feature is allowed.
 */
export function FeatureLockedBanner({ featureKey }: FeatureLockedBannerProps) {
  const t = useI18n();
  const { isFeatureAllowed, getRequiredPlan, isLoading } = useFeatureGate();
  const workspaceDialogService = useService(WorkspaceDialogService);

  const openBilling = useCallback(() => {
    workspaceDialogService.open('setting', { activeTab: 'workspace:billing' });
  }, [workspaceDialogService]);

  // Don't render while loading or if feature is allowed
  if (isLoading || isFeatureAllowed(featureKey)) {
    return null;
  }

  const plan = getRequiredPlan(featureKey);

  // If no required plan is known, the feature gate data is incomplete — don't render
  if (!plan) return null;

  return (
    <div className={styles.featureLockedBanner}>
      <InformationFillDuotoneIcon className={styles.featureLockedIcon} />
      <span className={styles.featureLockedText}>
        {t['com.streamlain.payment.feature-locked.message']({ plan })}
      </span>
      <Button variant="plain" onClick={openBilling}>
        {t['com.streamlain.payment.feature-locked.upgrade']()}
      </Button>
    </div>
  );
}
